import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { FirestorePathsService } from './firestorePaths.service';
import { FirestoreService } from '../lib/firestore.service';
import { SessionService } from './session.service';
import type { TMessage, TMessageStatus, TRole } from '@shared/models';

/**
 * MessageCenterService
 * 
 * Manages application messages displayed in the Message Center.
 * Messages are stored in Firestore at: accounts/{accountNumber}/messages
 * 
 * Message Targeting:
 * - Messages can target specific roles (manager, driver, serviceAdvisor, accountant)
 * - Messages can target a specific user by email
 * - Messages with no audience/recipientEmail are visible to ALL users
 * 
 * Use Cases:
 * - Trial period expiration warnings
 * - System maintenance announcements
 * - New feature announcements
 * - Billing/payment alerts
 * - Account status changes
 * - Promotional messages
 * - Role-specific onboarding tasks
 */
@Injectable({
    'providedIn': 'root'
})
export class MessageCenterService {
    constructor(
        private firestoreService: FirestoreService,
        private firestorePathsService: FirestorePathsService,
        private sessionService: SessionService
    ) {
    }

    /**
     * Returns a nullable/empty message object with default values.
     */
    public getNullable(): TMessage {
        const message: TMessage = {
            'uid': '',
            'title': '',
            'summary': '',
            'body': '',
            'type': 'info',
            'priority': 'normal',
            'status': 'unread',
            'createdDate': new Date()
        };
        return message;
    }

    /**
     * Returns an observable of all messages for the current account.
     */
    public getObservable(): Observable<TMessage[]> {
        const path = this.firestorePathsService.getMessagesPath();
        const observable = this.firestoreService.getObservable(path) as Observable<TMessage[]>;
        return observable;
    }

    /**
     * Returns an observable of messages filtered for the current user.
     * Messages are filtered based on the user's role and email.
     */
    public getFilteredObservable(): Observable<TMessage[]> {
        return this.getObservable().pipe(
            map(messages => this.filterForCurrentUser(messages))
        );
    }

    /**
     * Filters messages for the current user based on their role and email.
     * 
     * A message is visible to the current user if:
     * 1. No targeting is specified (audience is empty/undefined AND recipientEmail is empty/undefined), OR
     * 2. The user's role is in the audience array, OR
     * 3. The user's email matches the recipientEmail
     * 
     * @param messages - All messages for the account
     * @returns Messages that should be visible to the current user
     */
    public filterForCurrentUser(messages: TMessage[]): TMessage[] {
        const currentUser = this.sessionService.getCurrentUser();
        if (!currentUser) {
            return [];
        }

        const userRole: TRole = currentUser.role;
        const userEmail: string = currentUser.email?.toLowerCase() || '';

        return messages.filter(message => {
            const hasAudience = message.audience && message.audience.length > 0;
            const hasRecipient = message.recipientEmail && message.recipientEmail.trim().length > 0;

            // If no targeting specified, message is for everyone
            if (!hasAudience && !hasRecipient) {
                return true;
            }

            // Check if user's role is in the audience
            if (hasAudience && message.audience.includes(userRole)) {
                return true;
            }

            // Check if user's email matches recipientEmail
            if (hasRecipient && message.recipientEmail.toLowerCase() === userEmail) {
                return true;
            }

            // Message is targeted but not to this user
            return false;
        });
    }

    /**
     * Returns the count of unread messages for the current user.
     */
    public getUnreadCountForCurrentUser(messages: TMessage[]): number {
        const filteredMessages = this.filterForCurrentUser(messages);
        return filteredMessages.filter(m => m.status === 'unread').length;
    }

    /**
     * Returns a single message by its uid.
     */
    public async getByUid(uid: string): Promise<TMessage> {
        const path = this.firestorePathsService.getMessagesPath();
        return this.firestoreService.getByUid(path, uid) as Promise<TMessage>;
    }

    /**
     * Returns messages matching a specific field value.
     */
    public async getByFieldValue(fieldName: string, fieldValue: any): Promise<TMessage[]> {
        const path = this.firestorePathsService.getMessagesPath();
        return this.firestoreService.getByFieldValue(path, fieldName, fieldValue) as Promise<TMessage[]>;
    }

    /**
     * Returns all messages for the current account.
     */
    public async getAll(): Promise<TMessage[]> {
        const path = this.firestorePathsService.getMessagesPath();
        return this.firestoreService.getAll(path) as Promise<TMessage[]>;
    }

    /**
     * Returns all unread messages.
     */
    public async getUnread(): Promise<TMessage[]> {
        return this.getByFieldValue('status', 'unread');
    }

    /**
     * Adds a new message to Firestore.
     */
    public async add(message: TMessage): Promise<TMessage> {
        const path = this.firestorePathsService.getMessagesPath();
        return this.firestoreService.add(path, message) as Promise<TMessage>;
    }

    /**
     * Updates an existing message in Firestore.
     */
    public async update(message: TMessage): Promise<TMessage> {
        const path = this.firestorePathsService.getMessagesPath();
        return this.firestoreService.update(path, message) as Promise<TMessage>;
    }

    /**
     * Marks a message as read by updating its status in Firestore.
     */
    public async markAsRead(uid: string): Promise<void> {
        await this.updateStatus(uid, 'read');
    }

    /**
     * Marks a message as dismissed by updating its status in Firestore.
     */
    public async dismiss(uid: string): Promise<void> {
        await this.updateStatus(uid, 'dismissed');
    }

    /**
     * Deletes a message from Firestore.
     */
    public async delete(uid: string): Promise<void> {
        const path = this.firestorePathsService.getMessagesPath();
        return this.firestoreService.delete(path, uid);
    }

    /**
     * Updates the status of a message in Firestore.
     */
    private async updateStatus(uid: string, status: TMessageStatus): Promise<void> {
        const message = await this.getByUid(uid);
        if (message) {
            message.status = status;
            await this.update(message);
        }
    }

    /**
     * Sorts messages by priority (urgent first) then by date (newest first).
     */
    public sort(messages: TMessage[]): TMessage[] {
        const priorityOrder = { 'urgent': 0, 'high': 1, 'normal': 2, 'low': 3 };
        return [...messages].sort((a, b) => {
            const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
            if (priorityDiff !== 0) return priorityDiff;
            const dateA = a.createdDate instanceof Date ? a.createdDate.getTime() : 0;
            const dateB = b.createdDate instanceof Date ? b.createdDate.getTime() : 0;
            return dateB - dateA;
        });
    }
}
